import React from "react";
import { AbsoluteFill, interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { Backdrop, glow } from "./shared";

const SOURCES = [
  { icon: "📦", name: "GitHub",     c: COLORS.neonPurple, start: 20 },
  { icon: "💬", name: "Slack",      c: COLORS.neonPink,   start: 35 },
  { icon: "📘", name: "Confluence", c: COLORS.neonBlue,   start: 50 },
  { icon: "📄", name: "PDFs",       c: COLORS.neonYellow, start: 65 },
];

export const Scene2Scanning: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  const fade = interpolate(frame, [durationInFrames - 20, durationInFrames], [1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

  const labelSp = spring({ frame, fps, config: SPRING.gentle });
  const labelOp = interpolate(labelSp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  const cx = 960;
  const cy = 540;

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fade, overflow: "hidden" }}>
      <Backdrop frame={frame} accent={COLORS.neonCyan} />

      {/* Status label */}
      <div style={{
        position: "absolute", top: 200, left: 0, right: 0,
        textAlign: "center",
        opacity: labelOp,
      }}>
        <div style={{
          fontSize: 40,
          fontFamily: FONTS.mono,
          color: COLORS.neonCyan,
          letterSpacing: 2,
          textShadow: glow(COLORS.neonCyan, true),
          fontWeight: 700,
        }}>
          Searching 4 sources
          <span style={{ opacity: (frame % 30) / 30 }}>...</span>
        </div>
      </div>

      {/* Central EAKC brain */}
      <div style={{
        position: "absolute",
        left: cx - 130,
        top: cy - 130,
        width: 260,
        height: 260,
      }}>
        <div style={{
          width: "100%", height: "100%",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${COLORS.neonCyan}66, ${COLORS.neonCyan}15 55%, transparent)`,
          border: `3px solid ${COLORS.neonCyan}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 130,
          boxShadow: `0 0 100px ${COLORS.neonCyan}aa, inset 0 0 50px ${COLORS.neonCyan}44`,
          transform: `scale(${1 + Math.sin(frame * 0.12) * 0.04})`,
        }}>
          🧠
        </div>
      </div>

      {/* Source icons flying in */}
      {SOURCES.map((s, i) => {
        const angle = (i * Math.PI * 2) / SOURCES.length + Math.PI / 4;
        const startR = 700;
        const endR = 240;
        const sp = spring({ frame: frame - s.start, fps, config: SPRING.gentle });
        const r = interpolate(sp, [0, 1], [startR, endR], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
        const op = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
        const sx = cx + Math.cos(angle) * r;
        const sy = cy + Math.sin(angle) * r;

        return (
          <div key={s.name} style={{
            position: "absolute",
            left: sx - 70,
            top: sy - 60,
            width: 140,
            height: 120,
            opacity: op,
          }}>
            <div style={{
              width: "100%", height: "100%",
              background: `linear-gradient(135deg, ${s.c}22, ${COLORS.bgCard}ee)`,
              border: `2px solid ${s.c}`,
              borderRadius: 14,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              boxShadow: `0 0 30px ${s.c}77`,
            }}>
              <div style={{ fontSize: 44 }}>{s.icon}</div>
              <div style={{
                fontSize: 15,
                fontFamily: FONTS.mono,
                fontWeight: 700,
                color: s.c,
                textShadow: glow(s.c),
              }}>{s.name}</div>
            </div>
          </div>
        );
      })}

      {/* Connecting rays */}
      <svg style={{ position: "absolute", inset: 0, pointerEvents: "none" }} width={1920} height={1080}>
        {SOURCES.map((s, i) => {
          const angle = (i * Math.PI * 2) / SOURCES.length + Math.PI / 4;
          const sp = spring({ frame: frame - s.start - 10, fps, config: SPRING.gentle });
          const op = interpolate(sp, [0, 1], [0, 0.75], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          const dashOff = -(frame * 4) % 18;
          const outerR = 240;
          const sx = cx + Math.cos(angle) * outerR;
          const sy = cy + Math.sin(angle) * outerR;
          return (
            <line key={i}
              x1={cx} y1={cy}
              x2={sx} y2={sy}
              stroke={s.c}
              strokeWidth={3}
              strokeOpacity={op}
              strokeDasharray="10 8"
              strokeDashoffset={dashOff}
              style={{ filter: `drop-shadow(0 0 6px ${s.c})` }}
            />
          );
        })}
      </svg>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
